import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import WineListToolbar from '../components/toolbars/WineListToolbar';
import '../index.css';
import { WineTag } from '../components/WineTags';

const WineList = () => {
    const navigate = useNavigate(); // Moved inside component
    const [wines, setWines] = useState([]);
    const [search, setSearch] = useState('');
    const [page, setPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);
    const [isLoading, setIsLoading] = useState(true);
    const [varietals, setVarietals] = useState([]);
    const [vintages, setVintages] = useState([]);
    const [vineyards, setVineyards] = useState([]);
    const [setShowModal] = useState(false);
    const [showForm, setShowForm] = useState(false);
    const [activeFilter, setActiveFilter] = useState("1"); // "1" for active, "0" for archived, "all" for all wines
    const [selectedWines, setSelectedWines] = useState([]);
    const [showBulkTagModal, setShowBulkTagModal] = useState(false);
    const [availableTags, setAvailableTags] = useState([]);
    const [selectedTagID, setSelectedTagID] = useState('');
    const [bulkActionType, setBulkActionType] = useState('add');
    const [newWine, setNewWine] = useState({
        vintage: '',
        varietal: '',
        appellation: '',
        subAppellation: '',
        vineyardId: '',
        vineyardBlock: '',
        grower: '',
        companyId: '',
        varietalDetail: '',
        wineNotes: ''
    });

    // Generate vintages function
    const generateVintages = () => {
        const currentYear = new Date().getFullYear();
        const years = ['Non-Vintage'];
        for (let i = currentYear; i >= currentYear - 12; i--) {
            years.push(i.toString());
        }
        setVintages(years);
    };

    // Fetch wines function
    const fetchWines = async () => {
        try {
            // Trim and prepare the search term
            const trimmedSearch = search.trim();

            // Determine if the search term looks like a wine ID
            const isNumericSearch = /^\d+$/.test(trimmedSearch);

            const response = await axios.get(`/api/wines`, {
                params: {
                    page: page,
                    limit: 30,
                    search: trimmedSearch,
                    // If the search is numeric, prioritize wine ID search
                    searchType: isNumericSearch ? 'id' : 'text',
                    active: activeFilter === "all" ? undefined : activeFilter
                }
            });
            
            // Update state with response data, handling the new response format
            setWines(response.data.wines || []);
            setTotalPages(response.data.totalPages || 1);
            setIsLoading(false);
        } catch (error) {
            console.error('Error fetching wines:', error);
            setIsLoading(false);
        }
    };

// For fetching wines when page or search changes
useEffect(() => {
    fetchWines();
}, [page, search, activeFilter]);

// For fetching static data only once on component mount
useEffect(() => {
    const fetchInitialData = async () => {
        try {
            generateVintages();
            
            const [varietalResponse, vineyardResponse] = await Promise.all([
                axios.get('/api/varietals'),
                axios.get('/api/vineyards')
            ]);
            
            setVarietals(varietalResponse.data);
            setVineyards(vineyardResponse.data);
        } catch (error) {
            console.error('Error fetching initial data:', error);
        }
    };

    fetchInitialData();
}, []); // Empty dependency array

    const handleVineyardChange = (e) => {
        const selectedVineyardId = e.target.value;
        const selectedVineyard = vineyards.find(v => v.id.toString() === selectedVineyardId);
        
        if (selectedVineyard) {
            setNewWine({
                ...newWine,
                vineyardId: selectedVineyardId,
                appellation: selectedVineyard.vinAppellation || '',
                subAppellation: selectedVineyard.vinSubAppellation || '',
                vineyardBlock: selectedVineyard.vinBlock || '',
                grower: selectedVineyard.lastName ? `${selectedVineyard.firstName || ''} ${selectedVineyard.lastName}`.trim() : '',
                // Keep any existing values that shouldn't be changed
                vintage: newWine.vintage,
                varietal: newWine.varietal,
                varietalDetail: newWine.varietalDetail,
                wineNotes: newWine.wineNotes,
                companyId: selectedVineyard.companyID
            });
        } else {
            // Clear vineyard-related fields if no vineyard is selected
            setNewWine({
                ...newWine,
                vineyardId: '',
                appellation: '',
                subAppellation: '',
                vineyardBlock: '',
                grower: '',
                companyId: ''
            });
        }
    };

    const handleAddWine = async (e) => {
        e.preventDefault();
        try {
            const wineData = {
                vintage: newWine.vintage,
                varietal: newWine.varietal,
                appellation: newWine.appellation,
                grower: newWine.grower,
                vineyardID: newWine.vineyardId,
                vineyard: newWine.vineyardBlock, // Add this if needed
                varietalDetail: newWine.varietalDetail,
                wineNotes: newWine.wineNotes
            };
    
            await axios.post('/api/wines', wineData);
            setShowModal(false);
            setNewWine({
                vintage: '',
                varietal: '',
                appellation: '',
                subAppellation: '',
                vineyardId: '',
                vineyardBlock: '',
                grower: '',
                companyId: '',
                varietalDetail: '',
                wineNotes: ''
            });
            
            // Refresh the wine list and reset to first page
            setPage(1);
            await fetchWines();
        } catch (error) {
            console.error('Error adding new wine lot:', error);
        }
    };
    useEffect(() => {
        const fetchTags = async () => {
            try {
                const response = await axios.get('/api/wine-tags');
                setAvailableTags(response.data);
            } catch (error) {
                console.error('Error fetching tags:', error);
            }
        };
        
        fetchTags();
    }, []);
    
    // Add this handler function for bulk tagging
    const handleBulkTag = async () => {
        if (!selectedTagID || selectedWines.length === 0) {
            alert('Please select a tag and at least one wine');
            return;
        }
        
        try {
            await axios.post('/api/wines/bulk-tag', {
                wineIDs: selectedWines,
                tagID: selectedTagID,
                action: bulkActionType // 'add' or 'remove'
            });
            
            setShowBulkTagModal(false);
            setSelectedTagID('');
            
            // Refresh the wine list to show the updated tags
            fetchWines();
        } catch (error) {
            console.error(`Error ${bulkActionType === 'add' ? 'applying' : 'removing'} bulk tag:`, error);
            alert(`Failed to ${bulkActionType === 'add' ? 'apply' : 'remove'} tag from selected wines`);
        }
    };
    
    // Add this function for individual tag removal
    const handleRemoveTag = async (wineID, tagID) => {
        if (window.confirm('Are you sure you want to remove this tag?')) {
            try {
                await axios.delete(`/api/wines/${wineID}/tags/${tagID}`);
                fetchWines();
            } catch (error) {
                console.error('Error removing tag:', error);
                alert('Failed to remove tag');
            }
        }
    };

    if (isLoading) {
        return (
            <>
                <WineListToolbar 
                    onToggleForm={() => setShowForm(!showForm)}
                    showForm={showForm}
                    search={search}
                    onSearchChange={setSearch}
                    activeFilter={activeFilter}
                    onFilterChange={setActiveFilter}
                    selectedWines={selectedWines}
                    onShowBulkTagModal={() => setShowBulkTagModal(true)}
                />
                <div className="container mt-4">
                    <div className="d-flex justify-content-center align-items-center" style={{ height: '200px' }}>
                        <div className="spinner-border text-primary" role="status">
                            <span className="visually-hidden">Loading...</span>
                        </div>
                    </div>
                </div>
            </>
        );
    }
    return (
        <>
            <WineListToolbar 
                onToggleForm={() => setShowForm(!showForm)}
                showForm={showForm}
                search={search}
                onSearchChange={setSearch}
                activeFilter={activeFilter}
                onFilterChange={setActiveFilter}
                selectedWines={selectedWines}
                onShowBulkTagModal={() => setShowBulkTagModal(true)}
            />
            {/* Add New Wine Form */}
            {showForm && (
                <div className="card mb-4 add-wine">
                    <div className="card-header bg-light">
                        <h5 className="mb-0">Add New Wine Lot</h5>
                    </div>
                    <div className="card-body">
                        <form onSubmit={handleAddWine}>
                            <div className="row">
                                <div className="col-md-4 mb-3">
                                    <label className="form-label">Vintage</label>
                                    <select
                                        className="form-control"
                                        value={newWine.vintage}
                                        onChange={(e) => setNewWine({ ...newWine, vintage: e.target.value })}
                                    >
                                        <option value="">Select Vintage</option>
                                        {vintages.map((vintage, index) => (
                                            <option key={index} value={vintage}>{vintage}</option>
                                        ))}
                                    </select>
                                </div>

                                <div className="col-md-4 mb-3">
                                    <label className="form-label">Varietal</label>
                                    <select 
                                        className="form-control"
                                        value={newWine.varietal}
                                        onChange={(e) => setNewWine({ ...newWine, varietal: e.target.value })}
                                    >
                                        <option value="">Select Varietal</option>
                                        {varietals.map((varietal, index) => (
                                            <option key={index} value={varietal}>{varietal}</option>
                                        ))}
                                    </select>
                                </div>

                                <div className="col-md-4 mb-3">
                                    <label className="form-label">Wine Style</label>
                                    <select
                                        className="form-control"
                                        value={newWine.varietalDetail}
                                        onChange={(e) => setNewWine({ ...newWine, varietalDetail: e.target.value })}
                                    >
                                        <option value="">Select Wine Style</option>
                                        <option value="Red">Red</option>
                                        <option value="White">White</option>
                                        <option value="Rose">Rosé</option>
                                        <option value="Sweet">Sweet</option>
                                        <option value="Late Harvest">Late Harvest</option>
                                        <option value="Piquette">Piquette</option>
                                        <option value="Pet-Nat">Pet-Nat</option>
                                        <option value="Hard Press">Hard Press</option>
                                        <option value="Pomace">Pomace</option>
                                    </select>
                                </div>
                            </div>

                            <div className="row">
                                <div className="col-md-6 mb-3">
                                    <label className="form-label">Vineyard</label>
                                    <select
                                        className="form-control"
                                        value={newWine.vineyardId}
                                        onChange={handleVineyardChange}
                                    >
                                        <option value="">Select Vineyard</option>
                                        {vineyards.map((vineyard) => (
                                            <option key={vineyard.id} value={vineyard.id}>
                                                {vineyard.vineyardName} - {vineyard.lastName || ''} 
                                                {vineyard.vinBlock ? ` (Block ${vineyard.vinBlock})` : ''}
                                            </option>
                                        ))}
                                    </select>
                                </div>

                                <div className="col-md-6 mb-3">
                                    <label className="form-label">Grower</label>
                                    <input
                                        type="text"
                                        className="form-control"
                                        value={newWine.grower}
                                        readOnly
                                    />
                                </div>
                            </div>

                            <div className="row">
                                <div className="col-md-4 mb-3">
                                    <label className="form-label">Appellation</label>
                                    <input
                                        type="text"
                                        className="form-control"
                                        value={newWine.appellation}
                                        readOnly
                                    />
                                </div>

                                <div className="col-md-4 mb-3">
                                    <label className="form-label">Sub Appellation</label>
                                    <input
                                        type="text"
                                        className="form-control"
                                        value={newWine.subAppellation}
                                        readOnly
                                    />
                                </div>

                                <div className="col-md-4 mb-3">
                                    <label className="form-label">Vineyard Block</label>
                                    <input
                                        type="text"
                                        className="form-control"
                                        value={newWine.vineyardBlock}
                                        readOnly
                                    />
                                </div>
                            </div>

                            <div className="mb-3">
                                <label className="form-label">Wine Notes</label>
                                <textarea
                                    className="form-control"
                                    value={newWine.wineNotes}
                                    onChange={(e) => setNewWine({ ...newWine, wineNotes: e.target.value })}
                                    placeholder="Enter notes about this wine"
                                    rows="3"
                                />
                            </div>

                            <div className="d-flex justify-content-end gap-2">
                                <button 
                                    type="button" 
                                    className="btn btn-secondary"
                                    onClick={() => setShowForm(false)}
                                >
                                    Cancel
                                </button>
                                <button type="submit" className="btn btn-success">
                                    Add Wine Lot
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}
        {isLoading ? (
                <p>Loading...</p>
            ) : (
                <div className="container mt-4">
                    {/* Wine List Table */}
                    <div className="card">
                        <div className="card-body">
                            <div className="table-responsive">
                            <table className="table table-hover mb-0">
                                <thead>
                                    <tr>
                                    <th>Wine ID</th>
                                    <th>Vintage</th>
                                    <th>Varietal</th>
                                    <th>Grower</th>
                                    <th>Appell.</th>
                                    <th>Gal.</th>
                                    <th>Tags</th>
                                    <th width="40">
                                    <input 
                                        type="checkbox" 
                                        className="form-check-input"
                                        onChange={(e) => {
                                        if (e.target.checked) {
                                            setSelectedWines(wines.map(w => w.wineID));
                                        } else {
                                            setSelectedWines([]);
                                        }
                                        }}
                                    />
                                    </th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {wines.map((wine) => (
                                    <tr 
                                        key={wine.wineID}
                                        onClick={() => navigate(`/wines/${wine.wineID}`)}
                                        style={{ cursor: 'pointer' }}
                                        className="table-row-hover"
                                    >
                                        <td>{wine.wineID}</td>
                                        <td>{wine.vintage}</td>
                                        <td>{wine.varietal} {wine.barrelCount > 0 && (
                                            <span className="ms-2 badge bg-secondary rounded-pill text-white" 
                                                style={{ fontSize: '0.75rem', verticalAlign: 'middle' }}
                                                title={`${wine.barrelCount} ${wine.barrelCount === 1 ? 'barrel' : 'barrels'}`}>
                                            {wine.barrelCount}
                                            </span>
                                        )} </td>
                                        <td>{wine.grower}</td>
                                        <td>{wine.appellation}</td>
                                        <td>{wine.totalGallons || 0}</td>
                                        <td>
                                            {wine.tags && wine.tags.length > 0 ? (
                                                <div className="d-flex flex-wrap">
                                                {wine.tags.map(tag => (
                                                    <span 
                                                    key={tag.id} 
                                                    className="tag-removable"
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        handleRemoveTag(wine.wineID, tag.id);
                                                    }}
                                                    >
                                                    <WineTag tag={tag} />
                                                    <span className="tag-remove-icon">×</span>
                                                    </span>
                                                ))}
                                                </div>
                                            ) : (
                                                <small className="text-muted">-</small>
                                            )}
                                            </td>
                                        <td onClick={(e) => e.stopPropagation()}>
                                        <input 
                                            type="checkbox" 
                                            className="form-check-input"
                                            checked={selectedWines.includes(wine.wineID)}
                                            onChange={(e) => {
                                            if (e.target.checked) {
                                                setSelectedWines([...selectedWines, wine.wineID]);
                                            } else {
                                                setSelectedWines(selectedWines.filter(id => id !== wine.wineID));
                                            }
                                            }}
                                        />
                                        </td>
                                    </tr>
                                    ))}
                                </tbody>
                                </table>
                            </div>
                        </div>
                    </div>

                    {/* Pagination Controls */}
                    <div className="d-flex justify-content-center mt-4">
                        <nav aria-label="Page navigation">
                            <ul className="pagination">
                                <li className="page-item" onClick={() => setPage(page > 1 ? page - 1 : 1)}>
                                    <button className="page-link" type="button">Previous</button>
                                </li>
                                {[...Array(totalPages)].map((_, index) => (
                                    <li
                                        key={index}
                                        className={`page-item ${page === index + 1 ? 'active' : ''}`}
                                        onClick={() => setPage(index + 1)}
                                    >
                                        <button className="page-link" type="button">{index + 1}</button>
                                    </li>
                                ))}
                                <li className="page-item" onClick={() => setPage(page < totalPages ? page + 1 : totalPages)}>
                                    <button className="page-link" type="button">Next</button>
                                </li>
                            </ul>
                        </nav>
                    </div>
                </div>
            )}
            {/* Bulk Tag Modal */}
            {showBulkTagModal && (
                <>
                <div className="modal-backdrop fade show"></div>
                <div className="modal show" style={{ display: 'block' }} tabIndex="-1">
                    <div className="modal-dialog">
                        <div className="modal-content">
                            <div className="modal-header">
                                <h5 className="modal-title">Apply Tag to Selected Wines</h5>
                                <button
                                    type="button"
                                    className="btn-close"
                                    onClick={() => setShowBulkTagModal(false)}
                                ></button>
                            </div>
                            <div className="modal-body">
                                <p>Selected Wines: {selectedWines.length}</p>
                                
                                <div className="mb-3">
                                    <div className="form-check form-check-inline">
                                        <input
                                            className="form-check-input"
                                            type="radio"
                                            name="tagAction"
                                            id="addTagRadio"
                                            value="add"
                                            checked={bulkActionType === 'add'}
                                            onChange={() => setBulkActionType('add')}
                                        />
                                        <label className="form-check-label" htmlFor="addTagRadio">
                                            Add Tag
                                        </label>
                                    </div>
                                    <div className="form-check form-check-inline">
                                        <input
                                            className="form-check-input"
                                            type="radio"
                                            name="tagAction"
                                            id="removeTagRadio"
                                            value="remove"
                                            checked={bulkActionType === 'remove'}
                                            onChange={() => setBulkActionType('remove')}
                                        />
                                        <label className="form-check-label" htmlFor="removeTagRadio">
                                            Remove Tag
                                        </label>
                                    </div>
                                </div>
                                
                                <div className="mb-3">
                                    <label className="form-label">Select Tag</label>
                                    <select
                                        className="form-select"
                                        value={selectedTagID}
                                        onChange={(e) => setSelectedTagID(e.target.value)}
                                    >
                                        <option value="">Choose a tag...</option>
                                        {availableTags.map(tag => (
                                            <option key={tag.tagID} value={tag.tagID}>
                                                {tag.name}
                                            </option>
                                        ))}
                                    </select>
                                </div>
                                
                                {selectedTagID && (
                                    <div className="mb-3">
                                        <h6>Preview:</h6>
                                        <div className="d-flex gap-1">
                                            {availableTags
                                                .filter(tag => tag.tagID == selectedTagID)
                                                .map(tag => (
                                                    <WineTag key={tag.tagID} tag={tag} />
                                                ))
                                            }
                                        </div>
                                    </div>
                                )}
                            </div>
                            <div className="modal-footer">
                                <button
                                    type="button"
                                    className="btn btn-secondary"
                                    onClick={() => setShowBulkTagModal(false)}
                                >
                                    Cancel
                                </button>
                                <button
                                    type="button"
                                    className="btn btn-primary"
                                    onClick={handleBulkTag}
                                    disabled={!selectedTagID || selectedWines.length === 0}
                                >
                                    {bulkActionType === 'add' ? 'Apply Tag' : 'Remove Tag'}
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
                </>
            )}
            <style jsx>{`
                .table-row-hover:hover td {
                    background-color: rgba(0,0,0,.075);
                }
                .table-row-hover {
                    transition: background-color 0.2s ease;
                }
                .page-link {
                    cursor: pointer;
                }
                .page-item.disabled .page-link {
                    cursor: not-allowed;
                }
                  .tag-removable {
                    position: relative;
                    cursor: pointer;
                    margin-right: 4px;
                    margin-bottom: 4px;
                }
                .tag-removable:hover {
                    opacity: 0.8;
                }
                .tag-removable:hover .tag-remove-icon {
                    display: inline-block;
                }
                .tag-remove-icon {
                    display: none;
                    position: absolute;
                    top: -5px;
                    right: -5px;
                    background-color: rgba(0,0,0,0.5);
                    color: white;
                    border-radius: 50%;
                    width: 16px;
                    height: 16px;
                    line-height: 14px;
                    text-align: center;
                    font-size: 12px;
                }
            `}</style>
        </>
    );
};

export default WineList;